<template>
  <div>
    <ServicesHero
      v-if="!isAuthenticated"
      :file="brochureFileInfo"
      :image="heroImage"
      :initial-fields="initialFields"
      :title="titlePage"
      download-text="Methodology"
    >
      <template #extra-ctas>
        <ButtonBookADemo />
      </template>
    </ServicesHero>

    <div
      class="mx-auto flex w-full max-w-content-container flex-col justify-center gap-y-7 px-small pb-20 pt-8 md:items-start xl:pb-36 xl:pl-16 xl:pr-0 xl:pt-10"
    >
      <Breadcrumb v-if="isAuthenticated" :menu="breadcrumbStore.items" />
      <PriceAssessmentsSectionTitle
        :icon="icon"
        :title="titlePage"
        authorized
        class="mb-6"
        title-classes="!text-4xl"
      />
      <div v-if="!isAuthenticated" class="mb-[18px]">
        <DescriptionAndPackage
          :descriptions="descriptions"
          :services="services"
          :title="titlePackage"
        >
          <div v-if="descriptionImage" class="overflow-hidden rounded-lg">
            <img
              :src="descriptionImage"
              alt="description banner"
              class="h-full w-full"
            />
          </div>
        </DescriptionAndPackage>
        <IceBanner :market="initialFields[0]" />
        <div class="mt-9 flex flex-col gap-y-4">
          <h4 class="text-center text-xl font-bold md:text-left">
            Subscribe to gain access
          </h4>
          <ServicesGroupButtons
            :file="brochureFileInfo"
            :initial-fields="initialFields"
            class="md:w-fit"
          />
        </div>
      </div>
      <section class="w-full">
        <div class="justify-between md:flex flex-wrap items-start gap-x-4">
          <TheTabsBySlug
            :key="jwtToken"
            :class="{ 'mb-3': !isChartActiveTab }"
            class="grow"
            :tabs="tabs"
            @active-tab-changed="setActiveTab"
          />
          <div
            v-if="isCustomisationToolActiveTab"
            class="my-6 text-base font-semibold md:hidden lg:pb-0"
          >
            Benchmark’s flexible pricing tool provides estimated delivered costs
            of several assessed battery raw materials via shipping container to
            selected geographic locations. Our market assessed prices as well as
            third party information and market research is combined to create
            indicative prices.
          </div>

          <!-- CTAs -->
          <div v-if="!isChartActiveTab" class="flex gap-4 items-center mb-3">
            <MethodologyDropdown
              v-if="isAuthenticated"
              class="w-full md:w-auto"
              :brochure-file-info="brochureFileInfo"
              :customisation-tool-brochure-file-info="
                customisationToolBrochureFileInfo
              "
            />
          </div>
        </div>
        <slot />
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch } from 'vue'
import { PRICE_ASSESSMENTS } from '~/constants/services/constants'

import TheTabsBySlug from '~/components/common/TheTabsBySlug.vue'
import PriceAssessmentsSectionTitle from '~/components/price-assessments/SectionTitle.vue'
import ButtonBookADemo from '~/components/shared/common/ButtonBookADemo.vue'
import Breadcrumb from '~/components/shared/navigation/Breadcrumb.vue'
import DescriptionAndPackage from '~/components/shared/services/DescriptionAndPackage.vue'
import ServicesGroupButtons from '~/components/shared/services/GroupButtons.vue'
import ServicesHero from '~/components/shared/services/Hero.vue'
import IceBanner from '~/components/shared/services/IceBanner.vue'
import MethodologyDropdown from '~/components/shared/services/MethodologyDropdown.vue'

import type { TGeneral } from '~/src/types/common'
import type { TMarketServiceConfig } from '~/src/types/markets'
import { getPricesTabs } from '~/src/utils/tabs'
import useAuthStore from '~/stores/auth'
import { useBreadcrumbStore } from '~/stores/breadcrumb'
import useChart from '~/stores/charts'

const props = defineProps<{
  product: TMarketServiceConfig
  productData: TGeneral
  subscription: string
}>()

const initialFields = [props.product.slug, PRICE_ASSESSMENTS.slug]
const titlePage = `${props.product.name} Prices`
const titlePackage = 'Price Assessment Package'
const icon = props.product.iconColor
const heroImage = props.productData.hero
const services = props.productData.services
const descriptions = props.productData.descriptions
const descriptionImage = props.productData.descriptionImage

const brochureFileInfo = {
  ...(props.productData.brochureFile || {}),
  ID: useRuntimeConfig().public[props.productData.brochureFile?.EnvKey],
}

const customisationToolBrochureFileInfo = {
  ID: useRuntimeConfig().public.priceCustomisationToolMethodologyId,
  category: 'methodology-price-assessments',
  file_name: 'Benchmark-Price-Customisation-Tool-Methodology.pdf',
}

const chartStore = useChart()
const authStore = useAuthStore()
const breadcrumbStore = useBreadcrumbStore()
const { isAuthenticated } = storeToRefs(authStore)

const tabs = getPricesTabs({
  market: props.product,
  hideIndexTab: !props.productData.indexTab,
  hideCustomisationToolTab: !props.productData.customisationToolTab,
})

const activeTab = ref('')

const jwtToken = computed(() => authStore.jwtToken)
const isChartActiveTab = computed(() => activeTab.value === 'chart')
const isCustomisationToolActiveTab = computed(
  () => activeTab.value === 'price-customisation-tool',
)

const emit = defineEmits<{
  activeTabChanged: [tab: string]
}>()

const setActiveTab = (tab: string) => {
  activeTab.value = tab
  emit('activeTabChanged', tab)
}

watch(isAuthenticated, async () => {
  chartStore.setHashes([])
})
</script>
